// Voice-note recorder — a framework-free state machine around MediaRecorder so
// the flow is unit-testable (the React hook `use-voice-note-recorder` is a thin
// subscription wrapper). States:
//
//   idle → recording → preview → idle
//                 ↘ (cancel) ↗
//   any → error (mic denied / unsupported / recorder failure) → idle (dismiss)
//
// Recording prefers audio/mp4 (AAC) — the one container every consumer of the
// note can decode, including iOS AVPlayer, which has no webm/opus decoder —
// falling back to webm/opus where mp4 recording is unsupported (Firefox; those
// notes still transcribe and play on web/Android). A denied microphone lands in a quiet
// `error` state — the UI shows an inline notice, never a modal.

export type VoiceNoteStatus = 'idle' | 'starting' | 'recording' | 'preview' | 'error';
export type VoiceNoteErrorKind = 'denied' | 'unsupported' | 'failed';

export interface VoiceNoteClip {
  blob: Blob;
  // Base mime with any codec suffix stripped (`audio/webm`, `audio/mp4`) —
  // the wire mimeType for the attachment and upload-url mint.
  mimeType: string;
  // Elapsed recording time in whole seconds when Stop was pressed.
  durationSeconds: number;
  // The same elapsed time in milliseconds — what the send gate measures against
  // `MIN_SEND_DURATION_MS` and what gets stamped on the outgoing attachment's
  // `durationMs` (the phones stamp integer ms too).
  durationMs: number;
}

export interface VoiceNoteState {
  status: VoiceNoteStatus;
  seconds: number;
  // Recent mic amplitudes in [0,1], newest last — drives the recorder bar's
  // waveform, exactly as `use-dictation`'s `levels` drives the dictation bar.
  levels: number[];
  clip: VoiceNoteClip | null;
  errorKind: VoiceNoteErrorKind | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// The shared send/mode decisions — the web twin of native's `VoiceRecorderLogic`
// (iOS `Store/VoiceNoteRecorder.swift`, Android `features/chat/VoiceNote.kt`).
// Every constant here is part of that shared spec; keep the three aligned.
// ─────────────────────────────────────────────────────────────────────────────

/** A recording shorter than this on send is discarded, not sent (an accidental tap-tap has no
 *  usable audio). Shared constant — MUST match iOS `minSendDuration = 0.5` / Android
 *  `MIN_SEND_DURATION_MS = 500`. */
export const MIN_SEND_DURATION_MS = 500;

/** A recorded container smaller than this is an empty/broken take — discard, never send.
 *  Shared constant — MUST match both phones' `MIN_SEND_FILE_BYTES`. */
export const MIN_SEND_FILE_BYTES = 1_024;

/**
 * The full send-vs-discard decision for a stopped take, as the `durationMs` to stamp on the
 * outgoing attachment — or null to discard silently (too short, or an empty container). Identical
 * decision on both phones (`VoiceRecorderLogic.sendDurationMs`).
 */
export function sendDurationMs(elapsedMs: number, fileBytes: number): number | null {
  if (elapsedMs < MIN_SEND_DURATION_MS || fileBytes < MIN_SEND_FILE_BYTES) return null;
  return Math.max(0, Math.round(elapsedMs));
}

/**
 * The composer's single mic button is MODAL — its behavior is sampled from the composer text AT
 * TAP TIME (and latched for the session): blank/whitespace-only text records a VOICE NOTE; any
 * non-whitespace text toggles DICTATION (speech-to-text into the field). Shared 1:1 with both
 * phones (`VoiceRecorderLogic.micMode` / `composerMicMode`).
 */
export type ComposerMicMode = 'voiceNote' | 'dictation';

export function composerMicMode(text: string): ComposerMicMode {
  return text.trim().length === 0 ? 'voiceNote' : 'dictation';
}

const PREFERRED_MIME_TYPES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'] as const;

type MediaRecorderLike = {
  state: string;
  mimeType?: string;
  start: (timeslice?: number) => void;
  stop: () => void;
  ondataavailable: ((e: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: ((e: unknown) => void) | null;
};

type MediaRecorderCtor = {
  new (stream: MediaStream, options?: { mimeType?: string }): MediaRecorderLike;
  isTypeSupported?: (type: string) => boolean;
};

export interface VoiceNoteRecorderDeps {
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  mediaRecorder: MediaRecorderCtor | null;
  // Injectable clock for tests; defaults to setInterval/clearInterval.
  setInterval?: (fn: () => void, ms: number) => ReturnType<typeof setInterval>;
  clearInterval?: (handle: ReturnType<typeof setInterval>) => void;
  // Monotonic-ish clock for the ms-accurate elapsed time the send gate reads. The 1s tick only
  // drives the displayed timer; the gate is a sub-second rule, so it can't be derived from it.
  now?: () => number;
}

export function defaultVoiceNoteDeps(): VoiceNoteRecorderDeps {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const Recorder =
    typeof window !== 'undefined' ? ((window as unknown as { MediaRecorder?: MediaRecorderCtor }).MediaRecorder ?? null) : null;
  return {
    getUserMedia: (constraints) => {
      if (!nav?.mediaDevices?.getUserMedia) return Promise.reject(new Error('getUserMedia unavailable'));
      return nav.mediaDevices.getUserMedia(constraints);
    },
    mediaRecorder: Recorder,
  };
}

/**
 * Can this browser record a voice note at all — a MediaRecorder, a getUserMedia, and a container
 * it will actually encode. Resolved after mount (SSR must not read `window`), exactly as
 * dictation's `supported` is, so the mic's mode is decided on the client.
 */
export function voiceNoteSupported(): boolean {
  if (typeof window === 'undefined') return false;
  if (!navigator.mediaDevices?.getUserMedia) return false;
  return pickRecordingMimeType((window as unknown as { MediaRecorder?: MediaRecorderCtor }).MediaRecorder ?? null) !== null;
}

export function pickRecordingMimeType(Recorder: MediaRecorderCtor | null): string | null {
  if (!Recorder) return null;
  const supports = Recorder.isTypeSupported;
  if (!supports) return PREFERRED_MIME_TYPES[0];
  for (const type of PREFERRED_MIME_TYPES) {
    try {
      if (supports.call(Recorder, type)) return type;
    } catch {
      // treat a throwing probe as unsupported
    }
  }
  return null;
}

// Strip the `;codecs=…` suffix — the attachment's wire mimeType is the base type.
export function baseMimeType(mimeType: string): string {
  return mimeType.split(';')[0].trim() || 'audio/webm';
}

const isPermissionDenied = (err: unknown): boolean => {
  const name = (err as { name?: string } | null)?.name;
  return name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError';
};

// Rolling window of amplitude samples kept (newest last), sized for the widest composer — the
// waveform renders as many of the newest as fit its measured width. Same window as dictation's.
const LEVEL_WINDOW = 120;

export class VoiceNoteRecorder {
  private readonly deps: VoiceNoteRecorderDeps;
  private listeners = new Set<(state: VoiceNoteState) => void>();
  private state: VoiceNoteState = { status: 'idle', seconds: 0, levels: [], clip: null, errorKind: null };
  private recorder: MediaRecorderLike | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private mimeType = 'audio/webm';
  // Set by cancel() so the onstop handler discards instead of previewing.
  private discardOnStop = false;
  // Wall-clock start of the take; the clip's durationMs is measured off it.
  private startedAt = 0;
  private audioCtx: AudioContext | null = null;
  private rafHandle: number | null = null;

  constructor(deps: VoiceNoteRecorderDeps = defaultVoiceNoteDeps()) {
    this.deps = deps;
  }

  getState(): VoiceNoteState {
    return this.state;
  }

  subscribe(listener: (state: VoiceNoteState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setState(patch: Partial<VoiceNoteState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l(this.state));
  }

  async start(): Promise<void> {
    if (this.state.status === 'recording' || this.state.status === 'starting') return;
    const picked = pickRecordingMimeType(this.deps.mediaRecorder);
    if (!this.deps.mediaRecorder || !picked) {
      this.setState({ status: 'error', errorKind: 'unsupported', clip: null, seconds: 0 });
      return;
    }
    this.setState({ status: 'starting', seconds: 0, levels: [], clip: null, errorKind: null });
    let stream: MediaStream;
    try {
      stream = await this.deps.getUserMedia({ audio: true });
    } catch (err) {
      this.setState({ status: 'error', errorKind: isPermissionDenied(err) ? 'denied' : 'failed' });
      return;
    }
    // A cancel that raced the permission prompt: release the mic and stay idle.
    // (Read through getState() — the awaited gap invalidates TS's narrowing.)
    if (this.getState().status !== 'starting') {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    this.stream = stream;
    this.chunks = [];
    this.discardOnStop = false;
    this.mimeType = picked;
    let recorder: MediaRecorderLike;
    try {
      recorder = new this.deps.mediaRecorder(stream, { mimeType: picked });
    } catch {
      this.releaseStream();
      this.setState({ status: 'error', errorKind: 'failed' });
      return;
    }
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    recorder.onerror = () => {
      this.discardOnStop = true;
      this.stopTimer();
      this.releaseStream();
      this.recorder = null;
      this.setState({ status: 'error', errorKind: 'failed', clip: null, levels: [] });
    };
    recorder.onstop = () => {
      const durationMs = this.elapsedMs();
      this.stopTimer();
      this.releaseStream();
      this.recorder = null;
      if (this.discardOnStop) {
        this.setState({ status: 'idle', seconds: 0, levels: [], clip: null, errorKind: null });
        return;
      }
      const mime = baseMimeType(recorder.mimeType || this.mimeType);
      const blob = new Blob(this.chunks, { type: mime });
      this.chunks = [];
      if (blob.size === 0) {
        this.setState({ status: 'error', errorKind: 'failed', clip: null, levels: [] });
        return;
      }
      this.setState({
        status: 'preview',
        levels: [],
        clip: { blob, mimeType: mime, durationSeconds: this.state.seconds, durationMs },
      });
    };
    try {
      recorder.start(250);
    } catch {
      this.releaseStream();
      this.setState({ status: 'error', errorKind: 'failed' });
      return;
    }
    this.recorder = recorder;
    this.startedAt = (this.deps.now ?? Date.now)();
    const set = this.deps.setInterval ?? setInterval;
    this.timer = set(() => this.setState({ seconds: this.state.seconds + 1 }), 1000);
    this.setState({ status: 'recording', seconds: 0, levels: [] });
    this.startMeter(stream);
  }

  /** Elapsed time of the take in ms — what the send gate measures (the 1s tick is display only). */
  private elapsedMs(): number {
    if (!this.startedAt) return 0;
    return Math.max(0, (this.deps.now ?? Date.now)() - this.startedAt);
  }

  /**
   * Best-effort level meter: MediaRecorder exposes no amplitude data, so the captured stream is
   * tapped a second time purely to animate the recorder bar's bars (the same technique, and the
   * same gain/window, as `use-dictation`). Recording is unaffected if any of it is unavailable —
   * no AudioContext (SSR/jest), a suspended context, a browser without rAF.
   */
  private startMeter(stream: MediaStream): void {
    try {
      if (typeof window === 'undefined' || typeof requestAnimationFrame !== 'function') return;
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      const ctx = new Ctor();
      this.audioCtx = ctx;
      if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      let lastSample = 0;
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const x = (buf[i] - 128) / 128;
          sum += x * x;
        }
        const rms = Math.sqrt(sum / buf.length);
        const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        if (now - lastSample > 70) {
          lastSample = now;
          // Gain up quiet speech, clamp to 1 so the bars always move.
          const level = Math.min(1, rms * 3.2);
          this.setState({ levels: [...this.state.levels.slice(-(LEVEL_WINDOW - 1)), level] });
        }
        this.rafHandle = requestAnimationFrame(tick);
      };
      this.rafHandle = requestAnimationFrame(tick);
    } catch {
      /* the meter is decorative — the take is recording either way */
    }
  }

  private stopMeter(): void {
    if (this.rafHandle != null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this.rafHandle);
    this.rafHandle = null;
    void this.audioCtx?.close().catch(() => {});
    this.audioCtx = null;
  }

  // Stop recording and move to preview (the clip lands via onstop).
  stop(): void {
    if (this.state.status !== 'recording' || !this.recorder) return;
    try {
      this.recorder.stop();
    } catch {
      this.stopTimer();
      this.releaseStream();
      this.recorder = null;
      this.setState({ status: 'error', errorKind: 'failed' });
    }
  }

  // Discard everything (recording, preview, or error) and return to idle.
  cancel(): void {
    if (this.state.status === 'recording' && this.recorder) {
      this.discardOnStop = true;
      try {
        this.recorder.stop();
      } catch {
        this.stopTimer();
        this.releaseStream();
        this.recorder = null;
        this.setState({ status: 'idle', seconds: 0, levels: [], clip: null, errorKind: null });
      }
      return;
    }
    this.stopTimer();
    this.releaseStream();
    this.recorder = null;
    this.chunks = [];
    this.setState({ status: 'idle', seconds: 0, levels: [], clip: null, errorKind: null });
  }

  private stopTimer(): void {
    if (this.timer != null) {
      const clear = this.deps.clearInterval ?? clearInterval;
      clear(this.timer);
      this.timer = null;
    }
  }

  private releaseStream(): void {
    this.stopMeter();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }
}

export function formatVoiceSeconds(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
